import { useMemo } from 'react'
import { Button, Checkbox, FormControlLabel, FormGroup, FormLabel, Typography } from '@mui/material'
import { EPEAT_LABELS, ImpresCatalogItem, ImpresSupplierOffer } from './impresTypes'

export type ImpresFilterSelection = {
  suppliers: string[]
  manufacturers: string[]
  compliance: string[]
  epeat: string[]
}

export const EMPTY_FILTER_SELECTION: ImpresFilterSelection = {
  suppliers: [],
  manufacturers: [],
  compliance: [],
  epeat: [],
}

export const SUPPLIER_ORDER: ImpresSupplierOffer['supplierName'][] = ['TD Synnex', 'Ingram Micro', 'Carahsoft', 'Dell']
const COMPLIANCE_OPTIONS = ['TAA Compliant', 'Energy Star']
const EPEAT_OPTIONS = ['Gold', 'Silver', 'Bronze']

export function itemMatchesFilters(item: ImpresCatalogItem, selection: ImpresFilterSelection): boolean {
  if (selection.suppliers.length > 0 && !item.suppliers.some(s => selection.suppliers.includes(s.supplierName))) {
    return false
  }
  if (
    selection.manufacturers.length > 0 &&
    (!item.manufacturer || !selection.manufacturers.includes(item.manufacturer))
  ) {
    return false
  }
  if (selection.compliance.length > 0) {
    const matchesTaa = selection.compliance.includes('TAA Compliant') && item.taa === 'C'
    const matchesEnergyStar = selection.compliance.includes('Energy Star') && item.energyStar === 'C'
    if (!matchesTaa && !matchesEnergyStar) return false
  }
  if (selection.epeat.length > 0) {
    const label = item.epeat ? EPEAT_LABELS[item.epeat] : null
    if (!label || !selection.epeat.includes(label)) return false
  }
  return true
}

function deriveSuppliers(results: ImpresCatalogItem[]): string[] {
  const present = new Set<string>()
  for (const item of results) {
    for (const supplier of item.suppliers) present.add(supplier.supplierName)
  }
  return SUPPLIER_ORDER.filter(name => present.has(name))
}

function deriveManufacturers(results: ImpresCatalogItem[]): string[] {
  const present = new Set<string>()
  for (const item of results) {
    if (item.manufacturer) present.add(item.manufacturer)
  }
  return Array.from(present).sort((a, b) => a.localeCompare(b))
}

// Counts always reflect the full (unfiltered) result set, not the currently-filtered subset
function countBySupplier(results: ImpresCatalogItem[]): Partial<Record<string, number>> {
  const counts: Partial<Record<string, number>> = {}
  for (const item of results) {
    for (const supplier of item.suppliers) {
      counts[supplier.supplierName] = (counts[supplier.supplierName] ?? 0) + 1
    }
  }
  return counts
}

function countByManufacturer(results: ImpresCatalogItem[]): Partial<Record<string, number>> {
  const counts: Partial<Record<string, number>> = {}
  for (const item of results) {
    if (item.manufacturer) counts[item.manufacturer] = (counts[item.manufacturer] ?? 0) + 1
  }
  return counts
}

function countByCompliance(results: ImpresCatalogItem[]): Record<string, number> {
  const counts: Record<string, number> = { 'TAA Compliant': 0, 'Energy Star': 0 }
  for (const item of results) {
    if (item.taa === 'C') counts['TAA Compliant']++
    if (item.energyStar === 'C') counts['Energy Star']++
  }
  return counts
}

function countByEpeat(results: ImpresCatalogItem[]): Record<string, number> {
  const counts: Record<string, number> = { Gold: 0, Silver: 0, Bronze: 0 }
  for (const item of results) {
    const label = item.epeat ? EPEAT_LABELS[item.epeat] : null
    if (label && label in counts) counts[label]++
  }
  return counts
}

type FilterGroupProps = {
  label: string
  options: string[]
  counts: Partial<Record<string, number>>
  selected: string[]
  onToggle: (value: string) => void
}

function FilterGroup({ label, options, counts, selected, onToggle }: FilterGroupProps) {
  if (options.length === 0) return null
  return (
    <FormGroup className="impres-filter-group">
      <FormLabel className="impres-filter-group-label">{label}</FormLabel>
      {options.map(option => (
        <FormControlLabel
          key={option}
          control={<Checkbox checked={selected.includes(option)} onChange={() => onToggle(option)} />}
          label={`${option} (${counts[option] ?? 0})`}
        />
      ))}
    </FormGroup>
  )
}

type Props = {
  results: ImpresCatalogItem[] | null
  selection: ImpresFilterSelection
  onChange: (next: ImpresFilterSelection) => void
  onClearAll: () => void
}

export default function ImpresCatalogFilters({ results, selection, onChange, onClearAll }: Props) {
  const hasResults = (results?.length ?? 0) > 0
  const suppliers = useMemo(
    () => (hasResults ? deriveSuppliers(results as ImpresCatalogItem[]) : []),
    [results, hasResults],
  )
  const manufacturers = useMemo(
    () => (hasResults ? deriveManufacturers(results as ImpresCatalogItem[]) : []),
    [results, hasResults],
  )
  const supplierCounts = useMemo(() => countBySupplier(results ?? []), [results])
  const manufacturerCounts = useMemo(() => countByManufacturer(results ?? []), [results])
  const complianceCounts = useMemo(() => countByCompliance(results ?? []), [results])
  const epeatCounts = useMemo(() => countByEpeat(results ?? []), [results])

  const toggle = (key: keyof ImpresFilterSelection, value: string) => {
    const current = selection[key]
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
    onChange({ ...selection, [key]: next })
  }

  return (
    <div className="impres-filters">
      <div className="impres-filters-header">
        <Typography variant="h6">Filters</Typography>
        <Button size="small" onClick={onClearAll}>
          Clear All
        </Button>
      </div>
      {hasResults && (
        <>
          <FilterGroup
            label="Supplier"
            options={suppliers}
            counts={supplierCounts}
            selected={selection.suppliers}
            onToggle={value => toggle('suppliers', value)}
          />
          <FilterGroup
            label="Manufacturer"
            options={manufacturers}
            counts={manufacturerCounts}
            selected={selection.manufacturers}
            onToggle={value => toggle('manufacturers', value)}
          />
          <FilterGroup
            label="Compliance"
            options={COMPLIANCE_OPTIONS}
            counts={complianceCounts}
            selected={selection.compliance}
            onToggle={value => toggle('compliance', value)}
          />
          <FilterGroup
            label="EPEAT Rating"
            options={EPEAT_OPTIONS}
            counts={epeatCounts}
            selected={selection.epeat}
            onToggle={value => toggle('epeat', value)}
          />
        </>
      )}
    </div>
  )
}
